import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { normalizeSector } from "../utils/normalizeSector";
import {
  MapPin,
  Search,
  SlidersHorizontal,
  X,
  ChevronDown,
  Building2,
} from "lucide-react";
import { db } from "../firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import ilceKoyData from "../json/ilce-koyler.json";
import ilIlceData from "../json/il-ilceler.json";
import ulkeSehirData from "../json/ulke-sehir.json";

/* ---------- Ülkeler ---------- */
const COUNTRIES = (() => {
  const uniqueCountries = [...new Set(ulkeSehirData.map((item) => item.ulke))];
  const others = uniqueCountries
    .filter((c) => c !== "Türkiye")
    .sort((a, b) => a.localeCompare(b, "tr"));
  return ["Türkiye", ...others];
})();

const PRIORITY_CITIES = ["İstanbul", "Sivas", "Ankara", "İzmir"];

const TURKEY_CITIES = (() => {
  const allCities = ilIlceData.map((il) => il.text);
  const priority = allCities
    .filter((city) =>
      PRIORITY_CITIES.some(
        (p) => p.toLocaleLowerCase("tr") === city.toLocaleLowerCase("tr")
      )
    )
    .sort((a, b) => {
      const aIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === a.toLocaleLowerCase("tr")
      );
      const bIndex = PRIORITY_CITIES.findIndex(
        (p) => p.toLocaleLowerCase("tr") === b.toLocaleLowerCase("tr")
      );
      return aIndex - bIndex;
    });
  const others = allCities
    .filter((city) => !priority.includes(city))
    .sort((a, b) => a.localeCompare(b, "tr"));
  return [...priority, ...others];
})();

/* ---------- Mapper'lar ---------- */

function mapFirestoreFirm(doc) {
  const d = doc.data() || {};
  return {
    id: doc.id,
    source: "fb",
    firma: d.firmaAdi || d.firma || "Firma Adı",
    sektor: (d.secilenSektorler && d.secilenSektorler[0]) || d.sektor || "",
    secilenSektorler: d.secilenSektorler || [],
    altSektorler: d.altSektorler || [],
    il: d.sehir || d.il || "",
    ilce: d.ilce || "",
    sivasIlce: d.sivasIlce || "",
    adres: d.acikAdres || d.adres || "",
    koy: d.sivasKoy || "",
    gsm: d.gsm || "",
    telefon: d.telefon || "",
    fax: d.fax || "",
    email: d.email || "",
    web: d.web || "",
    linkedin: d.linkedin || "",
    logo: d.logoUrl || "",
    logoUrl: d.logoUrl || "",
    hakkinda: d.hakkinda || "",
    kurulusYili: d.kurulusYili || "",
    isletmeTuru: d.isletmeTuru || "",
    yetkiliKisi: d.yetkiliKisi || "",
    yetkiliPozisyon: d.yetkiliPozisyon || "",
    calismaSaatleri: d.calismaSaatleri || "",
    anahtarKelimeler: d.anahtarKelimeler || [],
    urunler: d.urunler || [],
    isGold: d.isGold || false,
    isVitrin: d.isVitrin || false,
    createdAt: d.createdAt || null,
  };
}

const normTR = (s = "") =>
  s
    .toString()
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim();

const toTitleTR = (s = "") =>
  s
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toLocaleUpperCase("tr") + w.slice(1) : ""))
    .join(" ");

/* ---------- Logo bileşeni — in-memory cache ile ---------- */
const logoCache = new Map();

const FirmaLogo = React.memo(({ logo, name }) => {
  const initials = (name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toLocaleUpperCase("tr") || "")
    .join("");

  const [status, setStatus] = useState(() => {
    if (!logo) return "none";
    if (logoCache.has(logo)) return logoCache.get(logo);
    return "loading";
  });

  const handleLoad = useCallback(() => {
    logoCache.set(logo, "ok");
    setStatus("ok");
  }, [logo]);

  const handleError = useCallback(() => {
    logoCache.set(logo, "error");
    setStatus("error");
  }, [logo]);

  if (!logo || status === "error" || status === "none") {
    return (
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center shrink-0 border border-slate-200">
        <span className="text-xs font-semibold text-slate-500 tracking-wide">
          {initials}
        </span>
      </div>
    );
  }

  return (
    <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border border-slate-200 bg-white">
      {status !== "ok" && (
        <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-200 animate-pulse" />
      )}
      <img
        src={logo}
        alt={name}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        decoding="async"
        className={`w-full h-full object-contain transition-opacity duration-200 ${
          status === "ok" ? "opacity-100" : "opacity-0 absolute"
        }`}
      />
    </div>
  );
});

/* ---------- Firma Kartı ---------- */
const FirmaCard = React.memo(({ f, onClick }) => {
  const location =
    [f.ilce, f.il].filter(Boolean).map(toTitleTR).join(", ") || "—";
  const sectorLabel = normalizeSector(f.sektor || "") || "—";

  return (
    <article
      onClick={() => onClick?.(f)}
      className="group bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3.5 hover:border-slate-300 hover:shadow-md transition-all duration-150 cursor-pointer"
    >
      <FirmaLogo logo={f.logo} name={f.firma} />

      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-slate-900 truncate leading-tight group-hover:text-slate-700 transition-colors">
          {f.firma}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5 truncate">{sectorLabel}</p>
        <div className="flex items-center gap-1 mt-1.5">
          <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
          <span className="text-xs text-slate-400 truncate">{location}</span>
        </div>
      </div>

      <ChevronDown className="w-4 h-4 text-slate-300 group-hover:text-slate-400 -rotate-90 shrink-0 transition-colors" />
    </article>
  );
});

/* ---------- Filtre Select ---------- */
const FilterSelect = ({
  value,
  onChange,
  disabled,
  children,
  className = "",
}) => (
  <div className={`relative ${className}`}>
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full appearance-none pl-3 pr-8 py-2 text-sm bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed transition"
    >
      {children}
    </select>
    <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
  </div>
);

/* ---------- Ana Sayfa ---------- */
const FirmsPage = () => {
  const [fbFirms, setFbFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [categoryPanelOpen, setCategoryPanelOpen] = useState(false);

  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState("");
  const [country, setCountry] = useState("Türkiye");
  const [city, setCity] = useState("");
  const [cityDistrict, setCityDistrict] = useState("");
  const [district, setDistrict] = useState("");
  const [village, setVillage] = useState("");
  const [altSektor, setAltSektor] = useState("");

  const [hasGsm, setHasGsm] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [villageSearch, setVillageSearch] = useState("");
  const [isVillageDropdownOpen, setIsVillageDropdownOpen] = useState(false);
  const villageDropdownRef = useRef(null);

  const isTurkiye = country === "Türkiye";

  const cityDistricts = useMemo(() => {
    if (!city || !isTurkiye) return [];
    const ilData = ilIlceData.find((il) => normTR(il.text) === normTR(city));
    if (!ilData) return [];
    return ilData.districts
      .map((d) => d.text)
      .sort((a, b) => a.localeCompare(b, "tr"));
  }, [city, isTurkiye]);

  const ilceler = useMemo(() => {
    const uniqueIlce = [...new Set(ilceKoyData.map((item) => item.ilce))];
    return uniqueIlce.sort((a, b) => a.localeCompare(b, "tr"));
  }, []);

  const koyler = useMemo(() => {
    if (!district) return [];
    const filteredKoy = ilceKoyData
      .filter((item) => normTR(item.ilce) === normTR(district))
      .map((item) => item.koy);
    return filteredKoy.sort((a, b) => {
      const aMerkez = normTR(a).includes("merkez");
      const bMerkez = normTR(b).includes("merkez");
      if (aMerkez && !bMerkez) return -1;
      if (!aMerkez && bMerkez) return 1;
      return a.localeCompare(b, "tr");
    });
  }, [district]);

  const filteredKoyler = useMemo(() => {
    if (!villageSearch) return koyler;
    return koyler.filter((koy) => normTR(koy).includes(normTR(villageSearch)));
  }, [koyler, villageSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        villageDropdownRef.current &&
        !villageDropdownRef.current.contains(e.target)
      )
        setIsVillageDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const keys = [
      "search",
      "country",
      "city",
      "cityDistrict",
      "district",
      "village",
    ];
    const vals = keys.map((k) => sessionStorage.getItem(`pref:${k}`) || "");
    if (vals[0]) setSearch(vals[0]);
    if (vals[1]) setCountry(vals[1]);
    if (vals[2]) setCity(vals[2]);
    if (vals[3]) setCityDistrict(vals[3]);
    if (vals[4]) setDistrict(vals[4]);
    if (vals[5]) setVillage(vals[5]);
    keys.forEach((k) => sessionStorage.removeItem(`pref:${k}`));

    const savedSector = sessionStorage.getItem("pref:sector") || "";
    const savedSortBy = sessionStorage.getItem("pref:sortBy") || "newest";
    const savedAltSektor = sessionStorage.getItem("pref:altSektor") || "";

    if (savedSector) {
      setSector(savedSector);
      sessionStorage.removeItem("pref:sector");
    }
    if (savedAltSektor) {
      setAltSektor(savedAltSektor);
      sessionStorage.removeItem("pref:altSektor");
    }

    if (savedSortBy !== "newest") {
      setSortBy(savedSortBy);
      sessionStorage.removeItem("pref:sortBy");
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    try {
      const q = query(collection(db, "firmalar"), orderBy("createdAt", "desc"));
      const unsub = onSnapshot(
        q,
        (snap) => {
          setFbFirms(snap.docs.map(mapFirestoreFirm));
          setLoading(false);
        },
        (err) => {
          console.error(err);
          setError("Firmalar yüklenirken bir hata oluştu.");
          setLoading(false);
        }
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      return () => unsub();
    } catch (e) {
      setError("Firmalar yüklenirken bir hata oluştu.");
      setLoading(false);
    }
  }, []);

  const merged = fbFirms;

  const categories = useMemo(() => {
    const map = new Map();
    merged.forEach((f) => {
      const sektorler = f.secilenSektorler?.length
        ? f.secilenSektorler
        : f.sektor
        ? [f.sektor]
        : [];
      const altlar = Array.isArray(f.altSektorler) ? f.altSektorler : [];
      sektorler.forEach((s) => {
        const label = s.trim();
        if (!label) return;
        if (!map.has(label)) map.set(label, { count: 0, altMap: new Map() });
        const entry = map.get(label);
        entry.count++;
        altlar.forEach((a) => {
          const alt = a.trim();
          if (alt) entry.altMap.set(alt, (entry.altMap.get(alt) || 0) + 1);
        });
      });
    });
    return Array.from(map, ([name, { count, altMap }]) => ({
      name,
      count,
      altSektorler: Array.from(altMap, ([n, c]) => ({
        name: n,
        count: c,
      })).sort((a, b) => b.count - a.count),
    })).sort((a, b) => b.count - a.count);
  }, [merged]);

  useEffect(() => {
    const fromStorage = sessionStorage.getItem("selectedCategory");
    if (fromStorage) {
      setSelectedCategory(fromStorage);
      sessionStorage.removeItem("selectedCategory");
    }
  }, []);

  const [searchQ, setSearchQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(normTR(search)), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = useMemo(() => {
    let arr = merged;
    const cityN = normTR(city);
    const cityDistrictN = normTR(cityDistrict);
    const districtN = normTR(district);
    const villageN = normTR(village);

    if (selectedCategory) {
      arr = arr.filter((f) => {
        const sektorler = f.secilenSektorler?.length
          ? f.secilenSektorler
          : [f.sektor];
        return sektorler.some(
          (s) => normTR(s || "") === normTR(selectedCategory)
        );
      });
    }
    if (sector) {
      arr = arr.filter((f) => {
        const sektorler = f.secilenSektorler?.length
          ? f.secilenSektorler
          : [f.sektor];
        return sektorler.some((s) => normTR(s || "").includes(normTR(sector)));
      });
    }

    if (altSektor) {
      arr = arr.filter((f) =>
        (Array.isArray(f.altSektorler) ? f.altSektorler : []).some(
          (s) => normTR(s) === normTR(altSektor)
        )
      );
    }

    if (cityN && isTurkiye)
      arr = arr.filter((f) => normTR(f.il || "").includes(cityN));
    if (cityDistrictN && isTurkiye)
      arr = arr.filter((f) => normTR(f.ilce || "").includes(cityDistrictN));
    if (districtN)
      arr = arr.filter((f) => normTR(f.sivasIlce || "").includes(districtN));
    if (villageN)
      arr = arr.filter((f) => normTR(f.koy || "").includes(villageN));
    if (hasGsm) arr = arr.filter((f) => (f.gsm || "").trim().length > 0);
    if (hasAddress) arr = arr.filter((f) => (f.adres || "").trim().length > 0);
    if (searchQ)
      arr = arr.filter((f) =>
        normTR(
          `${f.firma} ${f.sektor} ${(f.secilenSektorler || []).join(" ")} ${
            f.adres
          } ${f.il} ${f.ilce} ${f.koy} ${(f.anahtarKelimeler || []).join(" ")}`
        ).includes(searchQ)
      );

    if (sortBy === "newest")
      return [...arr].sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );
    if (sortBy === "name-asc")
      return [...arr].sort((a, b) =>
        (a.firma || "").localeCompare(b.firma || "", "tr")
      );
    if (sortBy === "name-desc")
      return [...arr].sort((a, b) =>
        (b.firma || "").localeCompare(a.firma || "", "tr")
      );
    return arr;
  }, [
    merged,
    selectedCategory,
    sector,
    country,
    city,
    cityDistrict,
    district,
    village,
    hasGsm,
    hasAddress,
    searchQ,
    sortBy,
    isTurkiye,
    altSektor,
  ]);

  const [page, setPage] = useState(1);
  const pageSize = 24;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(
    () => setPage(1),
    [
      selectedCategory,
      sector,
      country,
      city,
      cityDistrict,
      district,
      village,
      hasGsm,
      hasAddress,
      searchQ,
      sortBy,
      altSektor,
    ]
  );
  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page]
  );

  const activeChips = [
    selectedCategory && {
      k: "Kategori",
      v: selectedCategory,
      onClear: () => setSelectedCategory(""),
    },
    sector && { k: "Sektör", v: sector, onClear: () => setSector("") },
    altSektor && {
      k: "Alt Sektör",
      v: toTitleTR(altSektor),
      onClear: () => setAltSektor(""),
    },
    country !== "Türkiye" && {
      k: "Ülke",
      v: country,
      onClear: () => setCountry("Türkiye"),
    },
    city && isTurkiye && { k: "İl", v: city, onClear: () => setCity("") },
    cityDistrict &&
      isTurkiye && {
        k: "İl İlçesi",
        v: cityDistrict,
        onClear: () => setCityDistrict(""),
      },
    district && {
      k: "Hemşehri İlçe",
      v: district,
      onClear: () => setDistrict(""),
    },
    village && { k: "Hemşehri Köy", v: village, onClear: () => setVillage("") },
    hasGsm && { k: "GSM", v: "Var", onClear: () => setHasGsm(false) },
    hasAddress && { k: "Adres", v: "Var", onClear: () => setHasAddress(false) },
    searchQ && { k: "Arama", v: search, onClear: () => setSearch("") },
  ].filter(Boolean);

  const clearAll = () => {
    setSelectedCategory("");
    setSector("");
    setAltSektor("");
    setCountry("Türkiye");
    setCity("");
    setCityDistrict("");
    setDistrict("");
    setVillage("");
    setVillageSearch("");
    setHasGsm(false);
    setHasAddress(false);
    setSearch("");
    setSortBy("newest");
  };

  const handleCardClick = useCallback(
    (f) => {
      sessionStorage.setItem("pref:search", search);
      sessionStorage.setItem("pref:country", country);
      sessionStorage.setItem("pref:city", city);
      sessionStorage.setItem("pref:cityDistrict", cityDistrict);
      sessionStorage.setItem("pref:district", district);
      sessionStorage.setItem("pref:village", village);
      if (selectedCategory)
        sessionStorage.setItem("selectedCategory", selectedCategory);

      if (sector) sessionStorage.setItem("pref:sector", sector);
      if (altSektor) sessionStorage.setItem("pref:altSektor", altSektor);
      if (sortBy !== "newest") sessionStorage.setItem("pref:sortBy", sortBy);

      navigate(`/firms/${f.id}`, { state: { firm: f } });
    },
    [
      navigate,
      search,
      country,
      city,
      cityDistrict,
      district,
      village,
      selectedCategory,
      sector,
      sortBy,
      altSektor,
    ]
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-6 max-w-screen-xl">
        {/* Üst başlık */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">
            Firmalar
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            <span className="font-medium text-slate-700">
              {filtered.length.toLocaleString("tr")}
            </span>{" "}
            firma listeleniyor
          </p>
        </div>

        {/* Hata */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Arama + Filtreler barı */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-5">
          {/* Ana arama satırı */}
          <div className="p-3 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Firma adı, sektör veya adres ara…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent bg-slate-50 placeholder:text-slate-400"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Sıralama */}
            <FilterSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-36 shrink-0"
            >
              <option value="newest">En Yeni</option>
              <option value="name-asc">A → Z</option>
              <option value="name-desc">Z → A</option>
            </FilterSelect>

            {/* Filtre toggle */}
            <button
              onClick={() => setFiltersOpen((o) => !o)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-all shrink-0 ${
                filtersOpen || activeChips.length > 0
                  ? "bg-slate-900 text-white border-slate-900"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>Filtrele</span>
              {activeChips.length > 0 && (
                <span
                  className={`text-xs rounded-full px-1.5 py-0.5 font-medium ${
                    filtersOpen || activeChips.length > 0
                      ? "bg-white text-slate-900"
                      : "bg-slate-100"
                  }`}
                >
                  {activeChips.length}
                </span>
              )}
            </button>
          </div>

          {/* Genişleyen filtre alanı */}
          {filtersOpen && (
            <div className="border-t border-slate-100 p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
                <FilterSelect
                  value={sector}
                  onChange={(e) => setSector(e.target.value)}
                >
                  <option value="">Sektör</option>
                  {categories.map((c) => (
                    <option key={c.name} value={c.name}>
                      {toTitleTR(c.name)}
                    </option>
                  ))}
                </FilterSelect>

                <FilterSelect
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    setCity("");
                    setCityDistrict("");
                  }}
                >
                  {COUNTRIES.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </FilterSelect>

                <FilterSelect
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setCityDistrict("");
                  }}
                  disabled={!isTurkiye}
                >
                  <option value="">{isTurkiye ? "İl" : "Sadece TR"}</option>
                  {isTurkiye &&
                    TURKEY_CITIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                </FilterSelect>

                <FilterSelect
                  value={cityDistrict}
                  onChange={(e) => setCityDistrict(e.target.value)}
                  disabled={!city || !isTurkiye}
                >
                  <option value="">
                    {!isTurkiye ? "Sadece TR" : city ? "İl İlçesi" : "Önce il"}
                  </option>
                  {cityDistricts.map((d) => (
                    <option key={d} value={d}>
                      {toTitleTR(d)}
                    </option>
                  ))}
                </FilterSelect>

                <FilterSelect
                  value={district}
                  onChange={(e) => {
                    setDistrict(e.target.value);
                    setVillage(""); // ← buraya ekle
                    setVillageSearch(""); // ← buraya ekle
                  }}
                >
                  <option value="">Hemşehri İlçe</option>
                  {ilceler.map((d) => (
                    <option key={d} value={d}>
                      {toTitleTR(d)}
                    </option>
                  ))}
                </FilterSelect>

                {/* Köy arama */}
                <div ref={villageDropdownRef} className="relative">
                  <div className="relative flex items-center border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-slate-900 bg-white">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 pointer-events-none" />
                    <input
                      type="text"
                      value={village ? toTitleTR(village) : villageSearch}
                      onChange={(e) => {
                        setVillageSearch(e.target.value);
                        setVillage("");
                        setIsVillageDropdownOpen(true);
                      }}
                      onFocus={() => setIsVillageDropdownOpen(true)}
                      disabled={!district}
                      placeholder={district ? "Hemşehri Köy" : "Önce ilçe"}
                      className="w-full pl-8 pr-7 py-2 text-sm outline-none bg-transparent disabled:text-slate-400 disabled:cursor-not-allowed placeholder:text-slate-400"
                    />
                    {(village || villageSearch) && district && (
                      <button
                        onClick={() => {
                          setVillage("");
                          setVillageSearch("");
                        }}
                        className="absolute right-2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {isVillageDropdownOpen && district && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl max-h-52 overflow-y-auto z-50 border border-slate-200">
                      {filteredKoyler.length > 0 ? (
                        filteredKoyler.map((koy) => (
                          <div
                            key={koy}
                            onClick={() => {
                              setVillage(koy);
                              setVillageSearch("");
                              setIsVillageDropdownOpen(false);
                            }}
                            className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700 transition"
                          >
                            {toTitleTR(koy)}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-slate-400">
                          Sonuç bulunamadı
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Checkbox'lar */}
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hasGsm}
                    onChange={(e) => setHasGsm(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 accent-slate-900"
                  />
                  <span className="text-sm text-slate-600">GSM mevcut</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hasAddress}
                    onChange={(e) => setHasAddress(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 accent-slate-900"
                  />
                  <span className="text-sm text-slate-600">Adres mevcut</span>
                </label>
                {activeChips.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="ml-auto text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 transition"
                  >
                    <X className="w-3 h-3" /> Tümünü sıfırla
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Aktif chip'ler */}
          {activeChips.length > 0 && (
            <div
              className={`flex flex-wrap gap-1.5 px-3 pb-3 ${
                filtersOpen ? "" : "border-t border-slate-100 pt-3"
              }`}
            >
              {activeChips.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-slate-100 text-slate-700 border border-slate-200"
                >
                  <span className="font-medium">{c.k}:</span> {c.v}
                  <button
                    onClick={c.onClear}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-slate-200 transition"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* İçerik: Sidebar + Kartlar */}
        <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-5">
          {/* Mobil kategori butonu */}
          <div className="lg:hidden">
            <button
              onClick={() => setCategoryPanelOpen((o) => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl border border-slate-200 shadow-sm text-sm font-medium text-slate-700"
            >
              <span>
                📂 Kategoriler{" "}
                {selectedCategory && `· ${toTitleTR(selectedCategory)}`}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  categoryPanelOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {categoryPanelOpen && (
              <div className="mt-2 bg-white rounded-xl border border-slate-200 shadow-sm p-3">
                <button
                  onClick={() => {
                    setSelectedCategory("");
                    setAltSektor("");
                    setCategoryPanelOpen(false);
                  }}
                  className={`w-full text-left px-2.5 py-2 rounded-lg text-sm mb-0.5 transition flex items-center justify-between ${
                    selectedCategory === ""
                      ? "bg-slate-900 text-white font-medium"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <span>Tümü</span>
                  <span
                    className={`text-xs ${
                      selectedCategory === "" ? "opacity-70" : "text-slate-400"
                    }`}
                  >
                    {merged.length.toLocaleString("tr")}
                  </span>
                </button>
                <div className="max-h-64 overflow-auto space-y-0.5">
                  {categories.map((c) => (
                    <div key={c.name}>
                      <button
                        onClick={() => {
                          setSelectedCategory(c.name);
                          setAltSektor("");
                          setCategoryPanelOpen(false);
                        }}
                        className={`w-full text-left px-2.5 py-2 rounded-lg text-sm transition flex items-center justify-between gap-1 ${
                          selectedCategory === c.name
                            ? "bg-slate-900 text-white font-medium"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <span className="truncate text-xs">
                          {toTitleTR(c.name)}
                        </span>
                        <span
                          className={`text-xs shrink-0 ${
                            selectedCategory === c.name
                              ? "opacity-70"
                              : "text-slate-400"
                          }`}
                        >
                          {c.count}
                        </span>
                      </button>
                      {selectedCategory === c.name &&
                        c.altSektorler.length > 0 && (
                          <div className="ml-2 mt-0.5 mb-1 space-y-0.5 border-l-2 border-slate-200 pl-2">
                            {c.altSektorler.map((alt) => (
                              <button
                                key={alt.name}
                                onClick={() => {
                                  setAltSektor(
                                    altSektor === alt.name ? "" : alt.name
                                  );
                                  setCategoryPanelOpen(false);
                                }}
                                className={`w-full text-left px-2 py-1.5 rounded-md text-xs transition flex items-center justify-between gap-1 ${
                                  altSektor === alt.name
                                    ? "bg-amber-500 text-white font-medium"
                                    : "text-slate-600 hover:bg-slate-50"
                                }`}
                              >
                                <span className="truncate">
                                  {toTitleTR(alt.name)}
                                </span>
                                <span
                                  className={`shrink-0 ${
                                    altSektor === alt.name
                                      ? "opacity-70"
                                      : "text-slate-400"
                                  }`}
                                >
                                  {alt.count}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Kategori sidebar */}
          <aside className="hidden lg:block bg-white rounded-xl border border-slate-200 shadow-sm p-3 h-max lg:sticky lg:top-6">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest px-2 mb-2">
              Kategoriler
            </p>

            <button
              onClick={() => {
                setSelectedCategory("");
                setAltSektor("");
              }}
              className={`w-full text-left px-2.5 py-2 rounded-lg text-sm mb-0.5 transition flex items-center justify-between ${
                selectedCategory === ""
                  ? "bg-slate-900 text-white font-medium"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span>Tümü</span>
              <span
                className={`text-xs ${
                  selectedCategory === "" ? "opacity-70" : "text-slate-400"
                }`}
              >
                {merged.length.toLocaleString("tr")}
              </span>
            </button>

            <div className="max-h-[65vh] overflow-auto space-y-0.5 pr-0.5">
              {categories.map((c) => (
                <div key={c.name}>
                  {/* Ana kategori butonu */}
                  <button
                    onClick={() => {
                      setSelectedCategory(c.name);
                      setAltSektor("");
                    }}
                    className={`w-full text-left px-2.5 py-2 rounded-lg text-sm transition flex items-center justify-between gap-1 ${
                      selectedCategory === c.name
                        ? "bg-slate-900 text-white font-medium"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="truncate text-xs">
                      {toTitleTR(c.name)}
                    </span>
                    <span
                      className={`text-xs shrink-0 ${
                        selectedCategory === c.name
                          ? "opacity-70"
                          : "text-slate-400"
                      }`}
                    >
                      {c.count}
                    </span>
                  </button>

                  {/* Alt sektörler — sadece seçili kategoride göster */}
                  {selectedCategory === c.name && c.altSektorler.length > 0 && (
                    <div className="ml-2 mt-0.5 mb-1 space-y-0.5 border-l-2 border-slate-200 pl-2">
                      {c.altSektorler.map((alt) => (
                        <button
                          key={alt.name}
                          onClick={() =>
                            setAltSektor(altSektor === alt.name ? "" : alt.name)
                          }
                          className={`w-full text-left px-2 py-1.5 rounded-md text-xs transition flex items-center justify-between gap-1 ${
                            altSektor === alt.name
                              ? "bg-amber-500 text-white font-medium"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <span className="truncate">
                            {toTitleTR(alt.name)}
                          </span>
                          <span
                            className={`shrink-0 ${
                              altSektor === alt.name
                                ? "opacity-70"
                                : "text-slate-400"
                            }`}
                          >
                            {alt.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </aside>

          {/* Kart grid */}
          <main>
            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3.5 animate-pulse"
                  >
                    <div className="w-12 h-12 rounded-xl bg-slate-100 shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 bg-slate-100 rounded w-3/4" />
                      <div className="h-3 bg-slate-100 rounded w-1/2" />
                      <div className="h-3 bg-slate-100 rounded w-2/5" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {paged.map((f) => (
                  <FirmaCard key={f.id} f={f} onClick={handleCardClick} />
                ))}
                {paged.length === 0 && (
                  <div className="col-span-full flex flex-col items-center justify-center py-16 text-slate-400">
                    <Building2 className="w-10 h-10 mb-3 opacity-40" />
                    <p className="text-sm">Sonuç bulunamadı.</p>
                    <button
                      onClick={clearAll}
                      className="mt-3 text-xs text-slate-500 underline underline-offset-2 hover:text-slate-800"
                    >
                      Filtreleri temizle
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-1.5">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 text-sm rounded-lg border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition"
                >
                  ← Önceki
                </button>

                {/* Sayfa numaraları */}
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) pageNum = i + 1;
                  else if (page <= 4) pageNum = i + 1;
                  else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                  else pageNum = page - 3 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-9 h-9 text-sm rounded-lg border transition ${
                        page === pageNum
                          ? "bg-slate-900 text-white border-slate-900 font-medium"
                          : "border-slate-200 hover:bg-slate-50 text-slate-600"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 text-sm rounded-lg border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition"
                >
                  Sonraki →
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default FirmsPage;
